import { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import { cn } from '../../utils/cn';
import type { NavGroup as NavGroupType } from '../../types/navigation';
import type { UserRole } from '../../types/api';

interface NavGroupProps {
  group: NavGroupType;
  userRole: UserRole;
}

export function NavGroup({ group, userRole }: NavGroupProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const location = useLocation();

  const visibleItems = group.items.filter(
    item => !item.roles || item.roles.includes(userRole)
  );

  if (visibleItems.length === 0) return null;

  // Check if any child item is active
  const hasActiveChild = visibleItems.some(
    item => location.pathname === item.path || location.pathname.startsWith(item.path + '/')
  );

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
    return undefined;
  }, [isOpen]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    };
  }, []);

  const handleMouseEnter = () => {
    // Clear any pending close timeout
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    setIsOpen(true);
  };

  const handleMouseLeave = () => {
    // Delay closing to allow mouse to move to dropdown
    closeTimeoutRef.current = setTimeout(() => {
      setIsOpen(false);
    }, 200);
  };

  return (
    <div
      ref={dropdownRef}
      data-testid={`nav-group-${group.key}`}
      className="relative"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Dropdown trigger */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          'inline-flex items-center gap-1 px-3 py-2 text-sm font-medium rounded-md transition-colors',
          hasActiveChild || isOpen
            ? 'text-gray-900 bg-gray-100 dark:text-slate-100 dark:bg-slate-800'
            : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50 dark:text-slate-400 dark:hover:text-slate-100 dark:hover:bg-slate-800'
        )}
      >
        {group.label}
        <ChevronDown className={cn('w-4 h-4 transition-transform', isOpen && 'rotate-180')} />
      </button>

      {/* Dropdown menu */}
      {isOpen && (
        <div className="absolute left-0 top-full mt-1 w-56 bg-white rounded-lg shadow-xl dark:bg-slate-900 dark:shadow-slate-950/50 py-2 z-50">
          {visibleItems.map(item => {
            const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
            return (
              <Link
                key={item.key}
                to={item.path}
                onClick={() => setIsOpen(false)}
                data-testid={item.testId || `nav-item-${item.key}`}
                className={cn(
                  'flex items-center gap-3 px-4 py-2.5 text-sm transition-colors',
                  isActive
                    ? 'text-gray-900 bg-gray-100 font-medium dark:text-slate-100 dark:bg-slate-800'
                    : 'text-gray-700 hover:bg-gray-50 dark:text-slate-400 dark:hover:bg-slate-800'
                )}
              >
                <item.icon className="w-4 h-4 shrink-0" />
                <span className="flex-1">{item.label}</span>
                {item.badge === 'preview' && (
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-gray-700 dark:bg-slate-700 dark:text-slate-300">
                    Preview
                  </span>
                )}
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
